import { useState } from "react";
import { Box, Button, Divider, SheetContainer, Stack } from "@bookingcom/bui-react";
import type { Environment, Feature, FeatureOverrides } from "../../../types";
import { getFeatureOverridesFromUrl } from "../../../utils/features";
import { getObjectKeys } from "../../../object";
import { FeatureOverrideList } from "./FeatureOverrideList";
import { FeatureOverridePicker } from "./FeatureOverridePicker";
import styles from "./FeatureOverrideSheet.module.css";

export type FeatureOverrideSheetProps = {
  active: boolean;
  features: Environment["features"];
  initialOverrides?: FeatureOverrides;
  onApply: (overrides: FeatureOverrides) => void;
  onClose: () => void;
};

const areOverridesEqual = (left: FeatureOverrides, right: FeatureOverrides): boolean => {
  const leftEntries = Object.entries(left).sort(([leftFeature], [rightFeature]) =>
    leftFeature.localeCompare(rightFeature)
  );
  const rightEntries = Object.entries(right).sort(([leftFeature], [rightFeature]) =>
    leftFeature.localeCompare(rightFeature)
  );

  return JSON.stringify(leftEntries) === JSON.stringify(rightEntries);
};

export const FeatureOverrideSheet = ({
  active,
  features,
  initialOverrides,
  onApply,
  onClose,
}: FeatureOverrideSheetProps) => {
  const [initialState] = useState<FeatureOverrides>(() => initialOverrides ?? getFeatureOverridesFromUrl());
  const [overrides, setOverrides] = useState<FeatureOverrides>(initialState);

  const isDirty = !areOverridesEqual(initialState, overrides);

  const addOverride = (feature: Feature, enabled: boolean) =>
    setOverrides((currentOverrides) => ({ ...currentOverrides, [feature]: enabled }));

  const removeOverride = (feature: Feature) =>
    setOverrides((currentOverrides) => {
      const nextOverrides = { ...currentOverrides };
      delete nextOverrides[feature];
      return nextOverrides;
    });

  return (
    <SheetContainer
      active={active}
      position="center"
      size={960}
      innerClassName={styles.featureOverrideSheet}
      zIndex="aboveSheetContainer"
      stickyHeader
      title="Feature Flag Override"
      closeAriaLabel="Close feature flag override"
      onCloseTrigger={onClose}
      footer={
        <Stack direction="row" justifyContent="end" mixin={{ padding: 4 }}>
          <Button disabled={!isDirty} onClick={() => onApply(overrides)}>
            Apply
          </Button>
        </Stack>
      }
    >
      <Box padding={0}>
        <Stack gap={6}>
          <FeatureOverridePicker
            features={features}
            overriddenFeatures={getObjectKeys(overrides)}
            onAdd={addOverride}
          />
          <Divider />
          <FeatureOverrideList overrides={overrides} onChange={addOverride} onRemove={removeOverride} />
        </Stack>
      </Box>
    </SheetContainer>
  );
};
